"use client";

import { useRouter } from "next/navigation";
import Script from "next/script";
import { useEffect, useRef, useState } from "react";

import { useUsercentricsConsent } from "@/hooks/use-usercentrics-consent";
import { Button, Divider, Notification, TextD2C } from "@/scott/ui/elements";
import { useVariantSwitch } from "./product-variant-switch-context";

const USERCENTRICS_SMARTFIT_SERVICE_NAME = "Smartfit";
const USERCENTRICS_CATEGORY = "marketing";

interface ProductSizeFinderSmartfitProps {
  microcopies: {
    title: string;
    description: string;
    linkLabel: string;
    consentMessage: string;
    consentActionLabel: string;
  };
  productData: {
    name: string;
    ean: string;
    imageUrl?: string;
    price: number;
    productType?: string;
    apiKey: string;
    units?: "imperial" | "metric";
  };
  sizeOptions: Array<{
    ean: string;
    href: string;
    variantCode?: string;
  }>;
  onSelect?: (event: CustomEvent) => void;
  language: string;
}

export function ProductSizeFinderSmartfit(
  props: Readonly<ProductSizeFinderSmartfitProps>
) {
  const smartfitConsented = useUsercentricsConsent(
    USERCENTRICS_SMARTFIT_SERVICE_NAME,
    USERCENTRICS_CATEGORY
  );

  const { microcopies, productData, sizeOptions, onSelect } = props;
  const widgetRef = useRef<HTMLElement>(null);
  const router = useRouter();
  const switchVariant = useVariantSwitch();
  const [widgetReady, setWidgetReady] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: smartfitConsented controls when the widget mounts into the DOM
  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget) {
      return;
    }

    const isReady = () => {
      const button = widget.querySelector("button");
      return button && button?.style.visibility !== "hidden";
    };

    if (isReady()) {
      setWidgetReady(true);
      return;
    }

    const observer = new MutationObserver(() => {
      if (isReady()) {
        setWidgetReady(true);
        observer.disconnect();
      }
    });

    observer.observe(widget, {
      attributes: true,
      subtree: true,
      childList: true,
    });

    return () => observer.disconnect();
  }, [smartfitConsented]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: smartfitConsented controls when the widget mounts into the DOM
  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget) {
      return;
    }

    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{
        code: string;
        stock: boolean;
      }>;

      const match = sizeOptions.find(
        (opt) => opt.ean === customEvent.detail.code
      );

      if (match) {
        if (switchVariant && match.variantCode) {
          switchVariant(match.variantCode, match.href);
        } else {
          router.push(match.href, { scroll: false });
        }
      }

      onSelect?.(customEvent);
    };

    widget.addEventListener("select", handler);

    return () => widget.removeEventListener("select", handler);
  }, [onSelect, sizeOptions, router, switchVariant, smartfitConsented]);

  return (
    <>
      {/* Smartfit is a marketing service: load its widget only after consent. */}
      {smartfitConsented === true && (
        <Script
          src={`https://widget3.smartfit.online/?apiKey=${productData.apiKey}`}
          strategy="afterInteractive"
          type="module"
        />
      )}

      {smartfitConsented === false && (
        <>
          <Divider className="my-6" />

          <div className="space-y-3">
            <TextD2C style="bold" variant="p-small">
              {microcopies.title}
            </TextD2C>
            <TextD2C variant="p-small">{microcopies.description}</TextD2C>

            <Notification
              action={{
                label: microcopies.consentActionLabel,
                onClick: () => window.UC_UI?.showSecondLayer(),
              }}
              backgroundColor="alabastergrey"
              className="mt-4"
              message={microcopies.consentMessage}
            />
          </div>
        </>
      )}

      {smartfitConsented === true && (
        <div className={widgetReady ? "space-y-3" : "hidden"}>
          <Divider className="my-6" />

          <TextD2C style="bold" variant="p-small">
            {microcopies.title}
          </TextD2C>
          <TextD2C variant="p-small">{microcopies.description}</TextD2C>

          <sf-product-sizing
            global-id={productData.ean}
            image={productData.imageUrl}
            lang={props.language}
            name={productData.name}
            price={productData.price.toString()}
            product-type={productData.productType}
            ref={widgetRef}
            style={{ width: "100%" }}
            units={productData.units}
          >
            <Button
              className="custom-btn mt-3 w-full"
              style={{ visibility: "hidden" }}
              variant="outline"
            >
              {microcopies.linkLabel}
            </Button>
          </sf-product-sizing>
        </div>
      )}
    </>
  );
}
